import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, Coins, Users, ClipboardList, LogOut, Gem, Settings, LayoutTemplate, UserSquare2, CheckSquare, RefreshCw, UsersRound, Diamond, ShieldCheck, Truck, ReceiptText, Hammer, Wrench, BarChart3, BookOpen, FileText, CalendarClock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/AuthContext";

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/inventory", label: "Inventory", icon: Package, testid: "admin-nav-inventory" },
  { to: "/admin/rates", label: "Daily Rates", icon: Coins, testid: "admin-nav-rates" },
  { to: "/admin/diamond-rates", label: "Diamond Rates", icon: Diamond, testid: "admin-nav-diamond-rates" },
  { to: "/admin/suppliers", label: "Suppliers", icon: Truck, testid: "admin-nav-suppliers" },
  { to: "/admin/purchases", label: "Purchases", icon: ReceiptText, testid: "admin-nav-purchases" },
  { to: "/admin/karigars", label: "Karigars", icon: Wrench, testid: "admin-nav-karigars" },
  { to: "/admin/job-work", label: "Job Work", icon: Hammer, testid: "admin-nav-job-work" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, testid: "admin-nav-reports" },
  { to: "/admin/daybook", label: "Daily Daybook", icon: CalendarClock, testid: "admin-nav-daybook" },
  { to: "/admin/custom-requests", label: "Custom Build Requests", icon: Hammer, testid: "admin-nav-custom-requests" },
  { to: "/admin/customers", label: "Customer Approval", icon: ShieldCheck, testid: "admin-nav-customers" },
  { to: "/admin/users", label: "Users", icon: UsersRound, testid: "admin-nav-users" },
  { to: "/admin/content", label: "Content & Categories", icon: LayoutTemplate, testid: "admin-nav-content" },
  { to: "/admin/settings", label: "Business Settings", icon: Settings, testid: "admin-nav-settings" },
];

const CRM_NAV = [
  { to: "/crm/leads", label: "Leads", icon: UserSquare2, testid: "admin-nav-leads" },
  { to: "/crm/daily-tasks", label: "Daily Tasks", icon: CheckSquare, testid: "admin-nav-daily-tasks" },
  { to: "/crm/winback", label: "Inactive / Win-Back", icon: RefreshCw, testid: "admin-nav-winback" },
  { to: "/crm/orders", label: "Orders", icon: ClipboardList, testid: "admin-nav-orders" },
  { to: "/crm/invoices", label: "Invoices", icon: FileText, testid: "admin-nav-invoices" },
  { to: "/crm/partner-ledger", label: "Partner Ledger", icon: BookOpen, testid: "admin-nav-partner-ledger" },
];

export const AdminShell = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logoutAdmin } = useAuth();

  const isCrm = location.pathname.startsWith("/crm");
  const NAV = isCrm ? CRM_NAV : ADMIN_NAV;

  return (
    <div className="min-h-screen bg-background grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      <aside data-testid="admin-sidebar" className="hidden lg:flex flex-col border-r border-border/60 glass-panel">
        <div className="h-16 flex items-center px-6 border-b border-border">
          <span className="font-display text-xl text-[hsl(var(--primary))]">MAAHEY<span className="text-[hsl(var(--gold-dark))]">{isCrm ? "CRM" : "ERP"}</span></span>
        </div>
        <div className="px-3 pt-4">
          <div data-testid="panel-switcher" className="grid grid-cols-2 gap-1 rounded-lg bg-[hsl(var(--gold-light))] p-1">
            <NavLink to="/admin" data-testid="panel-switch-admin"
              className={() => `text-center text-xs font-medium py-1.5 rounded-md transition-colors duration-200 ${!isCrm ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-foreground/70 hover:text-foreground"}`}>
              Admin Panel
            </NavLink>
            <NavLink to="/crm/leads" data-testid="panel-switch-crm"
              className={() => `text-center text-xs font-medium py-1.5 rounded-md transition-colors duration-200 ${isCrm ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-foreground/70 hover:text-foreground"}`}>
              CRM Panel
            </NavLink>
          </div>
        </div>
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-[background-color,color] duration-200 ${isActive ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-foreground/80 hover:bg-[hsl(var(--gold-light))]"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>
        <div className="p-4 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2">{admin?.email}</div>
          <Button data-testid="admin-logout-button" variant="outline" size="sm" className="w-full gap-2" onClick={() => { logoutAdmin(); navigate("/admin/login"); }}><LogOut className="h-4 w-4" /> Logout</Button>
        </div>
      </aside>

      <div className="flex flex-col min-w-0">
        <header className="h-16 border-b border-border/60 glass-panel flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="lg:hidden font-display text-lg text-[hsl(var(--primary))]"><Gem className="h-5 w-5 inline" /> {isCrm ? "CRM" : "ERP"}</span>
            <h1 className="font-display text-xl sm:text-2xl">{title}</h1>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>View Store</Button>
            <Button data-testid="admin-logout-button-mobile" variant="ghost" size="icon" className="lg:hidden tap-target-44 text-muted-foreground" aria-label="Logout" onClick={() => { logoutAdmin(); navigate("/admin/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        {/* Mobile nav */}
        <div className="lg:hidden border-b border-border/60 glass-panel">
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-[hsl(var(--gold-light))] p-1 mx-4 mt-2">
            <NavLink to="/admin" className={() => `text-center text-xs font-medium py-1.5 rounded-md ${!isCrm ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-foreground/70"}`}>Admin Panel</NavLink>
            <NavLink to="/crm/leads" className={() => `text-center text-xs font-medium py-1.5 rounded-md ${isCrm ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-foreground/70"}`}>CRM Panel</NavLink>
          </div>
          <div className="flex gap-2 px-4 py-2 overflow-x-auto scrollbar-none scroll-bouncy whitespace-nowrap">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `px-3 py-1.5 rounded-lg text-xs shrink-0 ${isActive ? "bg-[hsl(var(--primary))] text-white" : "glass-card"}`}>{n.label}</NavLink>
            ))}
          </div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
